import { users } from './users';
import { employees } from './employees';
import { salaryStructures } from './salaries';
import { payslips } from './payslips';
import { loans } from './loans';
import { leaves } from './leaves';
import { attendance } from './attendance';
import { dashboardStats, payrollSummary } from './dashboard';
import { User, Employee, SalaryStructure, Payslip, Attendance, Leave, Loan, DashboardStats, PayrollSummary } from '@/types';
import { 
  getFromLocalStorage, 
  saveToLocalStorage, 
  syncMockApiData,
  updateItemInLocalStorage,
  addItemToLocalStorage,
  removeItemFromLocalStorage
} from '@/lib/utils';

// Initialize data stores with localStorage if available
const initEmployees = syncMockApiData<Employee>('employees', employees);
const initUsers = syncMockApiData<User>('users', users);
const initSalaryStructures = syncMockApiData<SalaryStructure>('salaryStructures', salaryStructures);
const initPayslips = syncMockApiData<Payslip>('payslips', payslips);
const initLoans = syncMockApiData<Loan>('loans', loans);
const initLeaves = syncMockApiData<Leave>('leaves', leaves);
const initAttendance = syncMockApiData<Attendance>('attendance', attendance);
const initDashboardStats = syncMockApiData<DashboardStats>('dashboardStats', [dashboardStats])[0];
const initPayrollSummary = syncMockApiData<PayrollSummary>('payrollSummary', payrollSummary);

// Mock API service with localStorage persistence
export const api = {
  // Auth
  login: (email: string, password: string): Promise<User | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedUsers = getFromLocalStorage<User[]>('users', initUsers);
        const user = storedUsers.find(u => u.email === email);
     
        
        if (user) {
          // Save login session
          saveToLocalStorage('currentUser', user);
        }
        
        resolve(user || null);
      }, 500);
    });
  },
  
  // Users
  getUsers: (): Promise<User[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedUsers = getFromLocalStorage<User[]>('users', initUsers);
        resolve(storedUsers);
      }, 500);
    });
  },
  
  // Employees
  getEmployees: (): Promise<Employee[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedEmployees = getFromLocalStorage<Employee[]>('employees', initEmployees);
        resolve(storedEmployees);
      }, 500);
    });
  },
  
  getEmployee: (id: string): Promise<Employee | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedEmployees = getFromLocalStorage<Employee[]>('employees', initEmployees);
        const employee = storedEmployees.find(e => e.id === id);
        resolve(employee || null);
      }, 300);
    });
  },
  
  createEmployee: (employee: Employee): Promise<Employee> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedEmployees = getFromLocalStorage<Employee[]>('employees', initEmployees);
        const updatedEmployees = [...storedEmployees, employee];
        saveToLocalStorage('employees', updatedEmployees, true);
        resolve(employee);
      }, 500);
    });
  },
  
  updateEmployee: (id: string, updates: Partial<Employee>): Promise<Employee | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const updatedEmployees = updateItemInLocalStorage<Employee>('employees', id, updates, true);
        if (!updatedEmployees) {
          resolve(null);
          return;
        }
        
        const updatedEmployee = updatedEmployees.find(e => e.id === id);
        resolve(updatedEmployee || null);
      }, 500);
    });
  },
  
  deleteEmployee: (id: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const result = removeItemFromLocalStorage<Employee>('employees', id, true);
        resolve(!!result);
      }, 500);
    });
  },
  
  // Salary
  getAllSalaryStructures: (): Promise<SalaryStructure[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedSalaries = getFromLocalStorage<SalaryStructure[]>('salaryStructures', initSalaryStructures);
        resolve(storedSalaries);
      }, 300);
    });
  },
  
  getEmployeeSalaryStructure: (employeeId: string): Promise<SalaryStructure | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedSalaries = getFromLocalStorage<SalaryStructure[]>('salaryStructures', initSalaryStructures);
        const salary = storedSalaries.find(s => s.employeeId === employeeId);
        resolve(salary || null);
      }, 300);
    });
  },
  
  updateSalaryStructure: (id: string, updates: Partial<SalaryStructure>): Promise<SalaryStructure | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const updatedStructures = updateItemInLocalStorage<SalaryStructure>('salaryStructures', id, updates, true);
        if (!updatedStructures) {
          resolve(null);
          return;
        }
        
        const updatedStructure = updatedStructures.find(s => s.id === id);
        resolve(updatedStructure || null);
      }, 500);
    });
  },
  
  // Payslips
  getEmployeePayslips: (employeeId: string): Promise<Payslip[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedPayslips = getFromLocalStorage<Payslip[]>('payslips', initPayslips);
        const employeePayslips = employeeId ? storedPayslips.filter(p => p.employeeId === employeeId) : storedPayslips;
        resolve(employeePayslips);
      }, 300);
    });
  },
  
  getAllPayslips: (): Promise<Payslip[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedPayslips = getFromLocalStorage<Payslip[]>('payslips', initPayslips);
        resolve(storedPayslips);
      }, 500);
    });
  },
  
  getPayslipById: (payslipId: string): Promise<Payslip | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedPayslips = getFromLocalStorage<Payslip[]>('payslips', initPayslips);
        const payslip = storedPayslips.find(p => p.id === payslipId);
        resolve(payslip || null);
      }, 300);
    });
  },
  
  createPayslip: (payslip: Payslip): Promise<Payslip> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        addItemToLocalStorage<Payslip>('payslips', payslip, true);
        resolve(payslip);
      }, 500);
    });
  },
  
  updatePayslip: (id: string, updates: Partial<Payslip>): Promise<Payslip | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const updatedPayslips = updateItemInLocalStorage<Payslip>('payslips', id, updates, true);
        if (!updatedPayslips) {
          resolve(null);
          return;
        }
        
        const updatedPayslip = updatedPayslips.find(p => p.id === id);
        resolve(updatedPayslip || null);
      }, 500);
    });
  },
  
  // Dashboard
  getDashboardStats: (): Promise<DashboardStats> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedStats = getFromLocalStorage<DashboardStats>('dashboardStats', initDashboardStats);
        resolve(storedStats);
      }, 500);
    });
  },
  
  getPayrollSummary: (): Promise<PayrollSummary[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedSummary = getFromLocalStorage<PayrollSummary[]>('payrollSummary', initPayrollSummary);
        resolve(storedSummary);
      }, 500);
    });
  },
  
  // Attendance
  getAttendance: (employeeId?: string, month?: string): Promise<Attendance[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedAttendance = getFromLocalStorage<Attendance[]>('attendance', initAttendance);
        let result = employeeId ? storedAttendance.filter(a => a.employeeId === employeeId) : storedAttendance;
        if (month) {
          result = result.filter(a => a.date.startsWith(month));
        }
        resolve(result);
      }, 300);
    });
  },
  
  createAttendance: (attendanceRecord: Attendance): Promise<Attendance> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        addItemToLocalStorage<Attendance>('attendance', attendanceRecord, true);
        resolve(attendanceRecord);
      }, 500);
    });
  },
  
  updateAttendance: (id: string, updates: Partial<Attendance>): Promise<Attendance | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const updatedRecords = updateItemInLocalStorage<Attendance>('attendance', id, updates, true);
        if (!updatedRecords) {
          resolve(null);
          return;
        }
        
        const updatedRecord = updatedRecords.find(a => a.id === id);
        resolve(updatedRecord || null);
      }, 500);
    });
  },
  
  deleteAttendance: (id: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const result = removeItemFromLocalStorage<Attendance>('attendance', id, true);
        resolve(!!result);
      }, 500);
    });
  },
  
  // Leave
  getEmployeeLeaves: (employeeId?: string): Promise<Leave[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedLeaves = getFromLocalStorage<Leave[]>('leaves', initLeaves);
        const employeeLeaves = employeeId ? storedLeaves.filter(l => l.employeeId === employeeId) : storedLeaves;
        resolve(employeeLeaves);
      }, 300);
    });
  },
  
  createLeaveRequest: (leave: Leave): Promise<Leave> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        addItemToLocalStorage<Leave>('leaves', leave, true);
        resolve(leave);
      }, 500);
    });
  },
  
  updateLeaveRequest: (id: string, updates: Partial<Leave>): Promise<Leave | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const updatedLeaves = updateItemInLocalStorage<Leave>('leaves', id, updates, true);
        if (!updatedLeaves) {
          resolve(null);
          return;
        }
        
        const updatedLeave = updatedLeaves.find(l => l.id === id);
        resolve(updatedLeave || null);
      }, 500);
    });
  },
  
  deleteLeaveRequest: (id: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const result = removeItemFromLocalStorage<Leave>('leaves', id, true);
        resolve(!!result);
      }, 500);
    });
  },
  
  // Loans
  getEmployeeLoans: (employeeId?: string): Promise<Loan[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const storedLoans = getFromLocalStorage<Loan[]>('loans', initLoans);
        const employeeLoans = employeeId ? storedLoans.filter(l => l.employeeId === employeeId) : storedLoans;
        resolve(employeeLoans);
      }, 300);
    });
  },
  
  createLoanRequest: (loan: Loan): Promise<Loan> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        addItemToLocalStorage<Loan>('loans', loan, true);
        resolve(loan);
      }, 500);
    });
  },
  
  updateLoanRequest: (id: string, updates: Partial<Loan>): Promise<Loan | null> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const updatedLoans = updateItemInLocalStorage<Loan>('loans', id, updates, true);
        if (!updatedLoans) {
          resolve(null);
          return;
        }
        
        const updatedLoan = updatedLoans.find(l => l.id === id);
        resolve(updatedLoan || null);
      }, 500);
    });
  },
  
  getEmployeeById: async (id: string) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const storedEmployees = getFromLocalStorage<Employee[]>('employees', initEmployees);
    const employee = storedEmployees.find(emp => emp.id === id);
    
    if (!employee) {
      throw new Error('Employee not found');
    }
    
    return employee;
  }
};
